import { useState } from 'react';
import { Alert, Button, Field, Input, Select, useConfirm } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useT } from '../i18n/index.js';

interface Timetable {
  id: string;
  routeId: string;
  busId: string;
  departureTime: string;
  daysOfWeek: number[];
  validFrom: string;
  validTo: string | null;
  active: boolean;
  route: { id: string; name: string };
  bus: { id: string; name: string; plateNumber: string };
  _count: { trips: number };
}

interface Conflict {
  id: string;
  departureTime: string;
  routeName: string;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function describeDays(days: number[]): string {
  if (days.length === 7) return 'Every day';
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.join(',') === '1,2,3,4,5') return 'Weekdays';
  if (sorted.join(',') === '0,6') return 'Weekends';
  return sorted.map((day) => DAY_LABELS[day]).join(', ');
}

export function Timetables() {
  const t = useT();
  const { tenant } = useSession();
  const timetables = useResource<Timetable[]>('/admin/timetables');
  const routes = useResource<Array<{ id: string; name: string }>>('/admin/routes');
  const buses = useResource<Array<{ id: string; name: string; plateNumber: string }>>('/admin/buses');

  const [form, setForm] = useState<Partial<Timetable> | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { ask, dialog } = useConfirm();

  const columns: Column<Timetable>[] = [
    {
      key: 'time',
      header: t('f.departs'),
      render: (t) => <span className="board-time text-base">{t.departureTime}</span>,
    },
    { key: 'route', header: t('f.route'), render: (t) => <span className="font-medium">{t.route.name}</span> },
    { key: 'bus', header: t('f.bus'), render: (t) => `${t.bus.name} · ${t.bus.plateNumber}` },
    { key: 'days', header: t('f.runs'), render: (t) => describeDays(t.daysOfWeek) },
    {
      key: 'window',
      header: t('f.valid'),
      render: (t) => (
        <span className="tabular" style={{ color: 'var(--color-muted)' }}>
          {t.validFrom.slice(0, 10)} → {t.validTo ? t.validTo.slice(0, 10) : 'open'}
        </span>
      ),
    },
    { key: 'trips', header: t('f.trips'), numeric: true, render: (t) => t._count.trips },
    {
      key: 'active',
      header: t('f.status'),
      render: (t) => (
        <span style={{ color: t.active ? 'var(--signal-departed)' : 'var(--color-faint)' }}>
          {t.active ? 'Active' : 'Paused'}
        </span>
      ),
    },
  ];

  function openNew() {
    const today = new Date().toISOString().slice(0, 10);
    setForm({ departureTime: '08:00', daysOfWeek: [1, 2, 3, 4, 5], validFrom: today, active: true });
    setConflicts([]);
    setNotice(null);
    setError(null);
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload = {
        routeId: form.routeId ?? '',
        busId: form.busId ?? '',
        departureTime: form.departureTime ?? '08:00',
        daysOfWeek: form.daysOfWeek ?? [],
        validFrom: (form.validFrom ?? '').slice(0, 10),
        validTo: form.validTo ? form.validTo.slice(0, 10) : null,
        active: form.active ?? true,
      };
      const result = form.id
        ? await api.put<{ conflicts: Conflict[]; tripsCreated: number; retiredTrips?: number; bookedTripIds?: string[] }>(
            `/admin/timetables/${form.id}`,
            payload,
          )
        : await api.post<{ conflicts: Conflict[]; tripsCreated: number }>('/admin/timetables', payload);

      setConflicts(result.conflicts ?? []);
      const booked = (result as { bookedTripIds?: string[] }).bookedTripIds ?? [];
      setNotice(
        `Saved. ${result.tripsCreated} departure${result.tripsCreated === 1 ? '' : 's'} generated` +
          (booked.length ? `, ${booked.length} booked departure(s) left untouched for you to review.` : '.'),
      );
      await timetables.reload();
      if (!result.conflicts?.length) setForm(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not save this timetable.'));
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(timetable: Timetable) {
    const confirmed = await ask({
      title: `Pause “${timetable.route.name} at ${timetable.departureTime}”?`,
      body: 'Upcoming departures with no bookings will be removed. Booked departures stay and are listed for you to handle.',
      confirmLabel: 'Pause timetable',
    });
    if (!confirmed) return;
    try {
      const result = await api.delete<{ cancelledUnbookedTrips: number; bookedTripIds: string[] }>(
        `/admin/timetables/${timetable.id}`,
      );
      setNotice(
        `Paused. ${result.cancelledUnbookedTrips} unbooked departure(s) removed` +
          (result.bookedTripIds.length
            ? `, ${result.bookedTripIds.length} booked departure(s) still scheduled.`
            : '.'),
      );
      await timetables.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not pause this timetable.'));
    }
  }

  const ready = (routes.data?.length ?? 0) > 0 && (buses.data?.length ?? 0) > 0;

  return (
    <Page
      title={t('nav.timetables')}
      description={t('tt.subtitle', { tz: tenant?.timezone ?? 'your timezone' })}
      actions={
        <Button onClick={openNew} disabled={!ready}>
          {t('tt.newTimetable')}
        </Button>
      }
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={timetables.data}
        rowKey={(t) => t.id}
        loading={timetables.loading}
        error={timetables.error}
        onRowClick={(t) => {
          setForm(t);
          setConflicts([]);
          setNotice(null);
        }}
        empty={{
          title: t('tt.noTimetables'),
          description: ready
            ? 'A timetable turns a route and a bus into repeating departures.'
            : 'Add a route and a bus first, then schedule them.',
        }}
      />

      {timetables.data && timetables.data.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {timetables.data
            .filter((t) => t.active)
            .map((t) => (
              <Button key={t.id} size="sm" variant="secondary" onClick={() => deactivate(t)}>
                Pause {t.route.name} {t.departureTime}
              </Button>
            ))}
        </div>
      )}

      <Drawer
        open={form !== null}
        title={form?.id ? 'Edit timetable' : 'New timetable'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setForm(null)}>
              {t('f.cancel')}
            </Button>
            <Button onClick={save} loading={saving}>
              {t('tt.saveTimetable')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <Alert tone="error">{error}</Alert>}

          {conflicts.length > 0 && (
            <Alert tone="warning" title={t('tt.busClash')}>
              <ul className="mt-1 list-disc pl-4">
                {conflicts.map((conflict) => (
                  <li key={conflict.id}>
                    {conflict.routeName} at {conflict.departureTime}
                  </li>
                ))}
              </ul>
              <p className="mt-1">
                {t('tt.savedAnyway')}
              </p>
            </Alert>
          )}

          <Field label={t('f.route')} htmlFor="tt-route">
            <Select
              id="tt-route"
              value={form?.routeId ?? ''}
              onChange={(e) => setForm({ ...form, routeId: e.target.value })}
            >
              <option value="">{t('trips.chooseRoute')}</option>
              {(routes.data ?? []).map((route) => (
                <option key={route.id} value={route.id}>
                  {route.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('f.bus')} htmlFor="tt-bus">
            <Select
              id="tt-bus"
              value={form?.busId ?? ''}
              onChange={(e) => setForm({ ...form, busId: e.target.value })}
            >
              <option value="">{t('trips.chooseBus')}</option>
              {(buses.data ?? []).map((bus) => (
                <option key={bus.id} value={bus.id}>
                  {bus.name} · {bus.plateNumber}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label={t('trips.departureTime')}
            htmlFor="tt-time"
            hint={`Local time in ${tenant?.timezone ?? 'your timezone'}.`}
          >
            <Input
              id="tt-time"
              type="time"
              value={form?.departureTime ?? '08:00'}
              onChange={(e) => setForm({ ...form, departureTime: e.target.value })}
            />
          </Field>

          <div>
            <p className="mb-1.5 text-sm font-medium">{t('tt.runsOn')}</p>
            <div className="flex flex-wrap gap-1.5">
              {DAY_LABELS.map((label, day) => {
                const selected = (form?.daysOfWeek ?? []).includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={selected}
                    onClick={() =>
                      setForm({
                        ...form,
                        daysOfWeek: selected
                          ? (form?.daysOfWeek ?? []).filter((d) => d !== day)
                          : [...(form?.daysOfWeek ?? []), day],
                      })
                    }
                    className="h-9 w-12 rounded-lg border text-xs font-medium"
                    style={{
                      borderColor: selected ? 'var(--color-primary)' : 'var(--color-line-strong)',
                      background: selected ? 'var(--color-primary-wash)' : 'transparent',
                      color: selected ? 'var(--color-primary)' : 'var(--color-text)',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('f.from')} htmlFor="tt-from">
              <Input
                id="tt-from"
                type="date"
                value={(form?.validFrom ?? '').slice(0, 10)}
                onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
              />
            </Field>
            <Field label={t('f.until')} htmlFor="tt-to" hint={t('tt.blankHint')}>
              <Input
                id="tt-to"
                type="date"
                value={(form?.validTo ?? '').slice(0, 10)}
                onChange={(e) => setForm({ ...form, validTo: e.target.value || null })}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form?.active ?? true}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            {t('tt.activeHint')}
          </label>
        </div>
      </Drawer>
      {dialog}
    </Page>
  );
}
